import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Phone, Calendar } from 'lucide-react';
import { Section, Button } from '../components/ui/Common.jsx';
import ResponsiveImage from '../components/ui/ResponsiveImage.jsx';
import { companyInfo } from '../data.js';

export default function MorsoeTuristfart() {
  return (
    <>
      <Helmet>
        <title>Morsø Turistfart | Din lokale transportpartner på Mors</title>
        <meta 
          name="description" 
          content="Morsø Turistfart - Din lokale transportpartner på Mors. Vi tilbyder professionel transport med taxaer og busser til både små og store opgaver." 
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[350px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <ResponsiveImage
            src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/morsa-1_1.png"
            alt="Morsø Turistfart busser og taxaer"
            className="h-full w-full object-cover"
            unoptimized
          />
          <div className="absolute inset-0 bg-slate-900/60" />
        </div>
        <div className="container relative z-10 mx-auto flex h-full items-end pb-16 px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/"
              className="mb-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-blue-400 hover:text-blue-300 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
            </Link>
            <h1 className="text-4xl font-bold text-white md:text-5xl lg:text-6xl">
              Morsø Turistfart
            </h1>
            <p className="mt-4 text-lg text-slate-200 max-w-xl">
              Transport med lokale rødder og personlig service
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <Section className="bg-white">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          {/* Left Column: Text Content */}
          <div className="lg:col-span-7 space-y-8">
            <div>
              <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
                Morsø Turistfart
              </span>
              <h2 className="mt-2 text-3xl font-bold text-slate-900 md:text-4xl">
                Din lokale transportpartner på Mors
              </h2>
              <p className="mt-4 text-xl text-slate-600 leading-relaxed font-medium">
                Når du vælger Morsø Turistfart, vælger du mere end bare transport. Du vælger en lokal virksomhed, der kender området, menneskerne og behovene.
              </p>
            </div>

            <div className="space-y-6 text-slate-600 leading-relaxed text-lg">
              <p>
                Vi holder til i Nykøbing Mors, og alle vores kørsler planlægges og styres lokalt. Det giver fleksibilitet, nærvær og hurtige beslutninger – til gavn for vores kunder hver eneste dag.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <div className="overflow-hidden shadow-md border border-slate-100">
                  <ResponsiveImage
                    src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/morsa-1_1.png"
                    alt="Morsø Turistfart busser og taxaer opstillet"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    unoptimized
                  />
                </div>
                <div className="overflow-hidden shadow-md border border-slate-100">
                  <ResponsiveImage
                    src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/morsa-3.png"
                    alt="Morsø Turistfart turistbus"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 768px) 100vw, 800px"
                  />
                </div>
              </div>

              <p>
                Med en moderne vognpark bestående af både taxaer og busser i forskellige størrelser kan vi tilbyde den rette løsning, uanset om du skal transporteres alene, med familien, kollegerne eller en større gruppe.
              </p>
              <p>
                Vores chauffører er lokale og kender vejene, virksomhederne og lokalsamfundet. Derfor får du ikke bare en chauffør – du får en serviceminded medarbejder, som er engageret i at give dig en tryg, behagelig og punktlig oplevelse.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <div className="overflow-hidden shadow-md border border-slate-100">
                  <ResponsiveImage
                    src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/morsa-5.png"
                    alt="Morsø Turistfart vognpark detalje"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 768px) 100vw, 800px"
                  />
                </div>
                <div className="overflow-hidden shadow-md border border-slate-100">
                  <ResponsiveImage
                    src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/morsa-2.png"
                    alt="Morsø Turistfart minibus og taxa"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 768px) 100vw, 800px"
                  />
                </div>
              </div>
            </div>

            {/* Highlighted Slogan Box */}
            <div className="bg-slate-50 border-l-4 border-blue-600 p-6 rounded-r-xl">
              <p className="text-lg font-semibold text-slate-800 italic">
                "Lokale chauffører. Lokale arbejdspladser. Lokal service."
              </p>
              <p className="mt-2 text-slate-600">
                Vi er klar til at køre dig sikkert frem – hvad enten turen går rundt på Mors, i Thy eller videre ud i landet.
              </p>
            </div>
          </div>

          {/* Right Column: Key Points & Image */}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-28">
            {/* Benefits Card */}
            <div className="bg-slate-900 text-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-xl font-bold mb-6 text-blue-400">
                Derfor vælger kunderne os:
              </h3>
              <ul className="space-y-4">
                {[
                  "Erfarne chauffører med lokalkendskab",
                  "Taxaer og busser til både små og store opgaver",
                  "Høj service og stor fleksibilitet",
                  "En virksomhed, der støtter og investerer i lokalområdet"
                ].map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500/20 text-blue-400">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                    <span className="text-slate-200 font-medium">{point}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col sm:flex-row gap-4">
                <Link to="/kontakt" className="flex-1">
                  <Button variant="blue" className="w-full justify-center gap-2">
                    <Calendar className="h-4 w-4" /> Book transport
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="flex-1">
                  <Button variant="outline" className="w-full justify-center gap-2 border-slate-700 hover:bg-slate-800 text-white">
                    <Phone className="h-4 w-4" /> Ring nu
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
